import type { LineItemSpecSnapshot, QuotationLineItemDto } from '../types/quotation.types';
import { isEditableRow, isSpecValidForRematch } from './lineItem';

/**
 * 14ykpqtw3hw — the web mirror of the cutting-list quantity rule (QUO-R-001/002/005), for instant feedback
 * while typing (UI gate: a visible change within ~100 ms). The server stays authoritative: every new line
 * arrives already normalized by CSC_CORE's CuttingListQuantityRule, and the quotation re-match returns the
 * normalized quantity. Register: docs/modules/quotation/rules.md. scripts/cuttingListQuantityParity.test.ts
 * runs the same cases as the C# tests, so the two sides cannot drift.
 *
 * Going forward only: nothing here runs on load or at save — never in wireToDraft, never in saveDraft.
 */

const PIECES = 'pcs';

/** QUO-R-001 — `pcs` in any case, blank or no unit at all counts as pieces. */
export function isPiecesUnit(unit: string | null | undefined): boolean {
  return unit == null || unit.trim() === '' || unit.trim().toLowerCase() === PIECES;
}

/** QUO-R-002 — `100.2` → `101`; `100` stays `100`. */
export function roundUpPieces(quantity: number): number {
  return Math.ceil(quantity);
}

/**
 * QUO-R-001 — a cutting-list piece line: complete specs (the re-match validator's predicate), counted in
 * pieces, and not a summary / attachment row. Independent of the match status.
 */
export function isCuttingListPieceLine(
  line: Pick<QuotationLineItemDto, 'diameterMm' | 'lengthM' | 'gradeCode' | 'unit' | 'matchStatus'>,
): boolean {
  return isEditableRow(line) && isSpecValidForRematch(line) && isPiecesUnit(line.unit);
}

/**
 * QUO-R-005 — what a commit of `line` (already carrying the typed / edited value) should store. Returns the
 * rounded-up quantity and the value it replaced, or null when there is nothing to round.
 */
export function roundedQuantityFor(
  line: Pick<QuotationLineItemDto, 'diameterMm' | 'lengthM' | 'gradeCode' | 'unit' | 'matchStatus' | 'quantity'>,
): { quantity: number; from: number } | null {
  const q = line.quantity;
  if (q == null || !Number.isFinite(q) || Number.isInteger(q)) return null;
  if (!isCuttingListPieceLine(line)) return null;
  return { quantity: roundUpPieces(q), from: q };
}

/** QUO-R-005 — the hint shown when a commit was rounded. */
export function roundedHint(quantity: number): string {
  return `Rounded up to ${formatQuantity(quantity)} pcs`;
}

/**
 * QUO-R-004 — record the customer's value on a snapshot that doesn't have one yet, but only when the value
 * being normalized IS the customer's (the snapshot still holds it). A value sales typed is not the customer's,
 * so it never becomes a source. quantity/unit are left as they are: Revert returns to the extraction.
 */
export function withSource(
  original: LineItemSpecSnapshot | null | undefined,
  fromQuantity: number | null,
  fromUnit: 'pcs' | 'kgs',
): LineItemSpecSnapshot | null | undefined {
  if (!original || original.sourceQuantity != null) return original;
  if (original.quantity !== fromQuantity || original.unit !== fromUnit || fromQuantity == null) return original;
  return { ...original, sourceQuantity: fromQuantity, sourceUnit: fromUnit };
}

/**
 * Store rule (14ykpqtw3hw, entry point 6) — a re-match reply may carry a server-normalized quantity/unit.
 * Adopt it ONLY if the line still holds exactly what was sent: a slow reply for 100.2 must not overwrite a
 * newer typed 50. Returns null when the reply is stale or changes nothing.
 */
export function adoptableRematchQuantity(
  current: Pick<QuotationLineItemDto, 'quantity' | 'unit'>,
  sent: { quantity: number | null; unit: 'pcs' | 'kgs' },
  reply: { quantity: number | null; unit: string },
): { quantity: number | null; unit: 'pcs' | 'kgs' } | null {
  if (!isUnchangedSince(current, sent)) return null;
  const unit: 'pcs' | 'kgs' = reply.unit === 'kgs' ? 'kgs' : 'pcs';
  if (reply.quantity === sent.quantity && unit === sent.unit) return null;
  return { quantity: reply.quantity, unit };
}

/** True when the line's quantity and unit are still what a request carried. */
export function isUnchangedSince(
  current: Pick<QuotationLineItemDto, 'quantity' | 'unit'>,
  sent: { quantity: number | null; unit: 'pcs' | 'kgs' },
): boolean {
  return (current.quantity ?? null) === (sent.quantity ?? null) && current.unit === sent.unit;
}

const quantityFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });

function formatQuantity(n: number): string {
  return quantityFormat.format(n);
}

/**
 * Requirement 6 — the muted note under the quantity ("asked 100.2" / "asked 51,366.07 kg") and the full
 * sentence for its native `title`. Null when the line shows the customer's value already (or has none).
 */
export function sourceNote(
  line: Pick<QuotationLineItemDto, 'quantity' | 'unit' | 'weightKg' | 'originalSpec'>,
): { label: string; title: string } | null {
  const o = line.originalSpec;
  if (!o || o.sourceQuantity == null || !o.sourceUnit) return null;
  if (o.sourceQuantity === line.quantity && o.sourceUnit === line.unit) return null;

  const asked = formatQuantity(o.sourceQuantity);
  if (o.sourceUnit === 'kgs') {
    // The conversion rate is the line's weight per piece, read back from its matched weight.
    const perPc =
      line.unit === 'pcs' && line.quantity != null && line.quantity > 0 && line.weightKg != null && line.weightKg > 0
        ? line.weightKg / line.quantity
        : null;
    const how = perPc != null ? `converted at ${formatQuantity(perPc)} kg/pc` : 'converted to pieces';
    return {
      label: `asked ${asked} kg`,
      title: `Customer asked ${asked} kg — ${how}, rounded up`,
    };
  }
  return { label: `asked ${asked}`, title: `Customer asked ${asked} pcs — rounded up` };
}
